// src/pages/JourneyPage.tsx
import React from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DiamondIcon from '@mui/icons-material/Diamond';
import PublicIcon from '@mui/icons-material/Public';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const JourneyPage: React.FC = () => {
  const theme = useTheme();

  const journeyMilestones = [
    {
      year: 'Early 2024',
      quarter: 'Q1 2024',
      title: 'The Spark of Inspiration',
      description: 'Discovery of the local talent within Mutare, Zimbabwe. Mothers who are also artisans most coming from disadvantaged backgrounds but with very valuable skills for the community.',
      detail: 'Initial meetings with local artisans, material sourcing from African markets, and trying to establish the Poreia philosophy.',
      image: '/images/crafthero.jpg',
      color: theme.palette.secondary.main,
      icon: <DiamondIcon />,
      achievements: [
        'First 10 design concepts created',
        'Partnership with 5 local artisans',
        'Material quality standards established'
      ]
    },
    {
      year: 'Mid 2024',
      quarter: 'Q2-Q3 2024',
      title: 'Prototype Revolution',
      description: 'The first 50 Poreia style bags were crafted, each telling a unique story. We faced challenges with bead sourcing and quality control, but each prototype brought us closer to perfection.',
      detail: 'Prototype development, artisan training programs, and establishing our signature beadwork techniques.',
      image: '/images/prototype.jpg',
      color: theme.palette.primary.main,
      icon: <GroupsIcon />,
      achievements: [
        '30 bags handcrafted',
        'Established 3 artisans as lead craftsmen',
        'Developed 15+ unique bead patterns'
      ]
    },
    {
      year: 'End 2024',
      quarter: 'Q4 2024',
      title: 'Brand Awakening',
      description: 'Poreia registration, and a beginning of formalization and building the entity behind the brand.',
      detail: 'Brand launch,first public exhibition, and initial marketing campaigns.',
      image: '/images/brandawakening.jpg',
      color: theme.palette.success.main,
      icon: <PublicIcon />,
      achievements: [
        'Official brand launch event',
        'Website launched',
        'First exhibition in Mutare'
      ]
    },
    {
      year: '2025',
      quarter: 'Throughout 2025',
      title: 'Recognition & Building Momentum',
      description: 'Our first international order from Idaho, USA marked a turning point. Which then prompted us to expand to online marketplaces..',
      detail: 'First international orders, expansion to global markets, and recognition',
      image: '/images/shipping.jpg',
      color: theme.palette.warning.main,
      icon: <AccessTimeIcon />,
      achievements: [
        'First international order (USA)',
        'Expanded to online marketplaces'
      ]
    },
    {
      year: '2026',
      quarter: 'Q1 2026 - Present',
      title: 'Evolution & Expansion',
      description: 'Added new product lines including clutches, totes, and shoulder bags. Introduced variations with different bead patterns and colors. Established sustainable sourcing practices and artisan fair-trade agreements.',
      detail: 'New product lines, sustainable practices, artisan empowerment programs, and technological integration.',
      image: '/images/newlines.jpg',
      color: theme.palette.info.main,
      icon: <TrendingUpIcon />,
      achievements: [
        'Launched 5 new bag categories',
        'Established fair-trade agreements',
        'Integrated e-commerce technology'
      ]
    }
  ];

  const journeyStats = [
    { number: '500+', label: 'Artisan Hours', description: 'Collective craftsmanship' },
    { number: '15+', label: 'Unique Designs', description: 'Signature patterns' },
    { number: '8+', label: 'Countries', description: 'Global reach' },
    { number: '1000+', label: 'Bags Crafted', description: 'Handmade with love' }
  ];

  // Custom Timeline Component
  const CustomTimeline = () => {
    return (
      <Box sx={{ position: 'relative', my: 8 }}>
        {/* Vertical line for timeline */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '40px', md: '50%' },
            top: 0,
            bottom: 0,
            width: '3px',
            backgroundColor: alpha(theme.palette.secondary.main, 0.3),
            transform: { md: 'translateX(-50%)' }
          }}
        />

        {journeyMilestones.map((milestone, index) => {
          const isEven = index % 2 === 0;
          
          return (
            <Box
              key={milestone.year}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: isEven ? 'row' : 'row-reverse' },
                alignItems: { xs: 'flex-start', md: 'center' },
                mb: 8,
                position: 'relative',
                gap: { xs: 2, md: 0 }
              }}
            >
              {/* Left side content (for even items on desktop) */}
              <Box
                sx={{
                  width: { xs: '100%', md: '45%' },
                  textAlign: { xs: 'left', md: isEven ? 'right' : 'left' },
                  pr: { xs: 0, md: isEven ? 4 : 0 },
                  pl: { xs: 0, md: isEven ? 0 : 4 },
                  order: { xs: 2, md: isEven ? 1 : 3 }
                }}
              >
                {isEven || window.innerWidth < theme.breakpoints.values.md ? (
                  <TimelineCard milestone={milestone} isLeft={true} />
                ) : null}
              </Box>

              {/* Center timeline dot and connector */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 'auto', md: '10%' },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  order: { xs: 1, md: 2 }
                }}
              >
                {/* Year indicator */}
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: '50%',
                    backgroundColor: milestone.color,
                    color: 'white',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 2,
                    border: `4px solid ${theme.palette.background.paper}`
                  }}
                >
                  <CalendarTodayIcon sx={{ mb: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {milestone.year}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {milestone.quarter.split(' ')[0]}
                  </Typography>
                </Paper>
              </Box>

              {/* Right side content (for odd items on desktop) */}
              <Box
                sx={{
                  width: { xs: '100%', md: '45%' },
                  textAlign: { xs: 'left', md: isEven ? 'left' : 'right' },
                  pl: { xs: 0, md: isEven ? 4 : 0 },
                  pr: { xs: 0, md: isEven ? 0 : 4 },
                  order: { xs: 2, md: isEven ? 3 : 1 }
                }}
              >
                {!isEven || window.innerWidth < theme.breakpoints.values.md ? (
                  <TimelineCard milestone={milestone} isLeft={false} />
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Timeline Card Component
  const TimelineCard = ({ milestone, isLeft }: { milestone: any, isLeft: boolean }) => {
    return (
      <motion.div
        initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor: 'white',
            borderRadius: 2,
            borderTop: `4px solid ${milestone.color}`,
            maxWidth: 400,
            ml: { xs: 0, md: isLeft ? 'auto' : 0 },
            mr: { xs: 0, md: isLeft ? 0 : 'auto' },
            mb: { xs: 2, md: 0 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '50%',
                backgroundColor: alpha(milestone.color, 0.1),
                color: milestone.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {milestone.icon}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Playfair Display', serif",
                color: theme.palette.primary.main,
              }}
            >
              {milestone.title}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.6 }}>
            {milestone.description}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon fontSize="small" /> Key Achievements:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {milestone.achievements.map((achievement: string, idx: number) => (
                <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                  <CheckCircleIcon fontSize="small" sx={{ color: milestone.color, mr: 1, mt: 0.3 }} />
                  <Typography variant="body2" color="text.secondary">
                    {achievement}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 2 }}>
            {milestone.detail}
          </Typography>
        </Paper>
      </motion.div>
    );
  };

  return (
    <Box>
      <Navbar />
      
      {/* Hero Section */}
      <Box
        sx={{
          height: '70vh',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(/images/lady.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ color: 'white', textAlign: 'center' }}>
            <Typography
              variant="h1"
              component={motion.h1}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{
                fontSize: { xs: '3rem', md: '5rem' },
                fontWeight: 400,
                letterSpacing: '0.1em',
                mb: 2,
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Our Journey
            </Typography>
            
            <Typography
              variant="h4"
              component={motion.h4}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 300,
                letterSpacing: '0.1em',
                mb: 4,
                fontFamily: "'Cormorant Garamond', serif",
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.4,
              }}
            >
              From a single sketch to global recognition—charting Poreia's remarkable evolution
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Journey Stats */}
      <Box sx={{ py: 6, backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 4,
            textAlign: 'center' 
          }}>
            {journeyStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    color: theme.palette.secondary.main,
                    mb: 1,
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {stat.description}
                </Typography>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Main Journey Timeline */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: "'Playfair Display', serif",
            mb: 2,
            textAlign: 'center',
          }}
        >
          Timeline of Transformation
        </Typography>
        
        <Typography
          variant="h5"
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            color: 'text.secondary',
            mb: 8,
            textAlign: 'center',
            maxWidth: 800,
            mx: 'auto',
          }}
        >
          Every milestone represents a step toward redefining African luxury
        </Typography>

        <CustomTimeline />
      </Container>

      {/* Enhanced Visual Timeline */}
      <Box sx={{ py: 8, backgroundColor: alpha(theme.palette.secondary.main, 0.03) }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Playfair Display', serif",
              mb: 6,
              textAlign: 'center',
            }}
          >
            Visual Chronicle
          </Typography>
          
          {journeyMilestones.map((milestone, index) => (
            <Box
              key={milestone.year}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                gap: 6,
                alignItems: 'center',
                mb: 8,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      mb: 2,
                      color: milestone.color,
                    }}
                  >
                    {milestone.year}
                  </Typography>
                  
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      mb: 3,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {milestone.title}
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: 'text.secondary',
                      mb: 3,
                    }}
                  >
                    {milestone.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {milestone.achievements.map((achievement, idx) => (
                      <Chip
                        key={idx}
                        label={achievement}
                        size="small"
                        sx={{
                          bgcolor: alpha(milestone.color, 0.1),
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </motion.div>
              </Box>
              
              <Box sx={{ flex: 1, position: 'relative' }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <Box
                    component="img"
                    src={milestone.image}
                    alt={milestone.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';
                    }}
                    sx={{
                      width: '100%',
                      height: 300,
                      objectFit: 'cover',
                      borderRadius: 2,
                      boxShadow: `20px 20px 0px ${alpha(milestone.color, 0.2)}`,
                    }}
                  />
                  
                  <Paper
                    sx={{
                      position: 'absolute',
                      bottom: -20,
                      right: index % 2 === 0 ? -20 : 'auto',
                      left: index % 2 === 1 ? -20 : 'auto',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: 3,
                      maxWidth: 200,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif" }}>
                      {milestone.quarter}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Milestone Achieved
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            </Box>
          ))}
        </Container>
      </Box>

      {/* Future Vision */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 4,
            textAlign: 'center',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Playfair Display', serif",
              mb: 3,
              color: theme.palette.primary.main,
            }}
          >
            The Road Ahead
          </Typography>
          
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              color: 'text.secondary',
              mb: 4,
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            "Our journey has just begun. By 2027, we aim to establish Poreia as a globally recognized symbol of African luxury, empowering over 100 artisans across the continent while maintaining our commitment to sustainability and cultural authenticity."
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", color: theme.palette.secondary.main }}>
              ✦ Global Retail Partnerships
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", color: theme.palette.secondary.main }}>
              ✦ Artisan Training Academies
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", color: theme.palette.secondary.main }}>
              ✦ Sustainable Innovation Hub
            </Typography>
          </Box>
        </Paper>

        {/* Call to Action */}
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Playfair Display', serif",
              mb: 3,
            }}
          >
            Join Our Continuing Journey
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.2rem',
              color: 'text.secondary',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Every Poreia purchase contributes to this story. Own a piece of our journey and help write the next chapter.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              href="/#/collections"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                px: 6,
                py: 1.5,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Shop Collections
            </Button>
            
            <Button
              variant="outlined"
              href="/#/story"
              size="large"
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                px: 6,
                py: 1.5,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              Read Our Story
            </Button>
          </Box>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default JourneyPage;